import { useState, useCallback } from "react";

export type GeoStatus = "idle" | "loading" | "success" | "error";

export interface GeoState {
  status: GeoStatus;
  lat: number | null;
  lng: number | null;
  error: string | null;
  requestLocation: () => void;
}

export function useGeolocation(): GeoState {
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("error");
      setError("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setStatus("success");
        setError(null);
      },
      (err) => {
        setStatus("error");
        setError(
          err.code === 1
            ? "Konum izni reddedildi. Tarayıcı ayarlarınızdan izin verebilirsiniz."
            : "Konum alınamadı. Lütfen tekrar deneyin.",
        );
      },
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 0 },
    );
  }, []);

  return { status, lat, lng, error, requestLocation };
}

/** Watch position continuously (for route tracking) */
export function useWatchPosition() {
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watchId, setWatchId] = useState<number | null>(null);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("error");
      setError("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }
    setStatus("loading");
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setStatus("success");
        setError(null);
      },
      (err) => {
        setStatus("error");
        setError(err.code === 1 ? "Konum izni reddedildi." : "Konum alınamadı.");
      },
      { enableHighAccuracy: true, maximumAge: 0 },
    );
    setWatchId(id);
  }, []);

  const stopWatching = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      setStatus("idle");
    }
  }, [watchId]);

  return { status, lat, lng, error, startWatching, stopWatching, isWatching: watchId !== null };
}
